import {
  AccommodationArticleDetails,
  ArticleType,
  ArticleTypeExternal,
  AutomobileArticleDetails,
  CheckoutSession,
  FlightArticleDetails,
  OnlineProductArticleDetails,
  ServiceArticleDetails,
  TravelArticleDetails,
} from '@nemuru-eng/js-api-client';

import { AccommodationItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Accommodation/entities/AccommodationItem';
import { AccommodationFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Accommodation/types/AccommodationFormItem';
import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { FlightItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Flight/entities/FlightItem';
import { FlightFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Flight/types/FlightFormItem';
import { ProductItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Product/entities/ProductItem';
import { ProductFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Product/types/ProductFormItem';
import { ServiceItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Service/entities/ServiceItem';
import { ServiceFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Service/types/ServiceFormItem';
import { TravelItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Travel/entities/TravelItem';
import { TravelFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Travel/types/TravelFormItem';
import { DefaultCartItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/constants/DefaultCartItem';
import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { Item } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/Item';
import { FormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/FormItem';
import { MinimalFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/types/MinimalFormItem';
import { CreditCardItem } from '@/components/modules/Application/IberiaCards/_shared/entities/CreditCardItem';
import { CreditCardFormItem } from '@/components/modules/Application/IberiaCards/_shared/schemas/CreditCardFormItem.schema';

export class ItemMap {
  private static addOldData<T extends FormItem>(item: MinimalFormItem, oldItem?: Item<FormItem>): T {
    if (!oldItem) return item as T;
    if (oldItem.data.type !== item.type) return item as T;
    return {
      ...oldItem.data,
      ...item,
    } as T;
  }

  public static toItem(item: MinimalFormItem, oldCartItem?: Item<FormItem>): Item<FormItem> {
    switch (item.type) {
      case ArticleType.FLIGHT:
        return new FlightItem(this.addOldData<FlightFormItem>(item, oldCartItem));
      case ArticleType.ACCOMMODATION:
        return new AccommodationItem(
          this.addOldData<AccommodationFormItem>(item, oldCartItem),
          oldCartItem instanceof AccommodationItem ? oldCartItem.additionalData : undefined,
        );
      case ArticleType.SERVICE:
        return new ServiceItem(this.addOldData<ServiceFormItem>(item, oldCartItem));
      case ArticleType.TRAVEL:
        return new TravelItem(this.addOldData<TravelFormItem>(item, oldCartItem));
      case ArticleType.AUTOMOBILE:
        return new AutomobileItem(this.addOldData<AutomobileFormItem>(item, oldCartItem), oldCartItem?.totalDiscount);
      case ArticleType.ONLINE_PRODUCT:
        return new ProductItem(this.addOldData<ProductFormItem>(item, oldCartItem));
      case ArticleType.CREDIT_CARD:
        return new CreditCardItem(this.addOldData<CreditCardFormItem>(item, oldCartItem));
      default:
        throw new Error(`Unknown item type: ${item.type}`);
    }
  }

  public static fromDialogToItem(item: FormItem, totalDiscount?: number): Item<FormItem> {
    if (item.type === ArticleType.FLIGHT) return new FlightItem(item);
    if (item.type === ArticleType.ACCOMMODATION) return new AccommodationItem(item);
    if (item.type === ArticleType.SERVICE) return new ServiceItem(item);
    if (item.type === ArticleType.TRAVEL) return new TravelItem(item);
    if (item.type === ArticleType.AUTOMOBILE) return new AutomobileItem(item, totalDiscount);
    if (item.type === ArticleType.ONLINE_PRODUCT) return new ProductItem(item);
    if (item.type === ArticleType.CREDIT_CARD) return new CreditCardItem(item);
    // @ts-expect-error Property 'type' does not exist on type 'never'.ts(2339)
    throw new Error(`Unknown item type: ${item.type}`);
  }

  public static toItems(items: MinimalFormItem[], oldCart?: CartEntity): Item<FormItem>[] {
    return items.map((item, i) => this.toItem(item, oldCart?.items[i]));
  }

  public static changeArticleType(item: FormItem): FormItem {
    return {
      ...DefaultCartItem,
      type: item.type,
      units: '1',
      description: item.description,
      vat: item.vat,
      price: item.price,
      ...((item.type === ArticleType.TRAVEL || item.type === ArticleType.SERVICE) && { endDate: null }), // reset repeated fields among different ArticleTypes
      ...(item.type === ArticleType.AUTOMOBILE && { isNewVehicle: false }), // set default value
    } as FormItem;
  }

  public static fromCheckoutSessionToItems(checkoutSession: CheckoutSession): Item<FormItem>[] {
    return checkoutSession.cart.orderLines.map((orderLine) => {
      const { units, article, totalDiscount } = orderLine;

      switch (article.type) {
        case ArticleTypeExternal.FLIGHT:
          return FlightItem.fromOrderLineDto(units, article as FlightArticleDetails);
        case ArticleTypeExternal.ACCOMMODATION:
          return AccommodationItem.fromOrderLineDto(units, article as AccommodationArticleDetails);
        case ArticleTypeExternal.SERVICE:
          return ServiceItem.fromOrderLineDto(units, article as ServiceArticleDetails);
        case ArticleTypeExternal.TRAVEL:
          return TravelItem.fromOrderLineDto(units, article as TravelArticleDetails);
        case ArticleTypeExternal.AUTOMOBILE:
          return AutomobileItem.fromOrderLineDto(units, article as AutomobileArticleDetails, totalDiscount?.amount);
        case ArticleType.ONLINE_PRODUCT:
          return ProductItem.fromOrderLineDto(units, article as OnlineProductArticleDetails);
        // This is not needed and implementation is complicated
        // case ArticleType.CREDIT_CARD:
        //   return CreditCardItem.fromOrderLineDto(units, article as CreditCardArticleDetails);
        default:
          throw new Error(`Unknown item type: ${article.type}`);
      }
    });
  }
}
